import React, { useState, useMemo, useEffect } from 'react';
import { useGame } from '../context/GameContext';
import { ENEMIES } from '../game/enemies';
import { loreFor } from '../game/lore';
import { CARDS, CARD_POOL_COMMON, CARD_POOL_UNCOMMON, CARD_POOL_RARE, CARD_POOL_DIVINE, cardRarity } from '../game/cards';
import { BLESSINGS, RARITY_COLORS } from '../game/blessings';
import GameCard from '../components/GameCard';
import CodexCard from '../components/CodexCard';
import DivineCollection from '../components/DivineCollection';
import { ArrowLeft, BookOpen, Swords, Layers, Sparkles, Trophy } from 'lucide-react';
import { readCardStats } from '../game/cardStats';

const RARITY_ORDER = { common: 0, uncommon: 1, rare: 2, legendary: 3, divine: 4 };
const RARITIES = ['all', 'common', 'uncommon', 'rare', 'legendary', 'divine'];
const TAGS = ['all', 'rizz', 'mog', 'ratio', 'copium', 'schizo', 'doom'];

export default function Codex() {
  const { profile, setScreen } = useGame();
  const [tab, setTab] = useState('cards');
  const entries = profile?.codex_entries || [];
  const enemies = Object.values(ENEMIES);

  // Build the master card list — one entry per base id (skip + upgrades in list, show them via preview)
  const allCards = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const [id, c] of Object.entries(CARDS)) {
      if (/\+$/.test(id)) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      list.push({
        id, name: c.name, art: c.art, cost: c.cost, type: c.type, desc: c.desc,
        tags: c.tags || [], rarity: cardRarity(id),
        upgradedId: CARDS[id + '+'] ? id + '+' : null,
      });
    }
    return list.sort((a, b) => (RARITY_ORDER[b.rarity]||0) - (RARITY_ORDER[a.rarity]||0) || a.name.localeCompare(b.name));
  }, []);

  const [rarityFilter, setRarityFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filteredCards = allCards.filter(c => {
    if (rarityFilter !== 'all' && c.rarity !== rarityFilter) return false;
    if (tagFilter !== 'all' && !c.tags.includes(tagFilter)) return false;
    if (typeFilter !== 'all' && (c.type || '').toLowerCase() !== typeFilter) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen w-full px-6 py-6" data-testid="codex-screen">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button className="brutal-btn text-sm" onClick={() => setScreen('menu')} data-testid="codex-back">
          <ArrowLeft size={16}/> BACK
        </button>
        <h1 className="font-display text-3xl sm:text-5xl font-black uppercase text-[color:var(--neon)] flex items-center gap-3">
          <BookOpen /> SLOP CODEX
        </h1>
        <div className="text-xs uppercase text-[color:var(--text-dim)]" data-testid="codex-progress">
          {tab === 'enemies' ? `${entries.length}/${enemies.length}` : `${allCards.length} CARDS`}
        </div>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
        <button
          className={`brutal-btn text-xs px-4 py-2 ${tab === 'cards' ? '' : 'opacity-50'}`}
          onClick={() => setTab('cards')}
          data-testid="codex-tab-cards">
          <Layers size={14} className="inline mr-1"/> CARDS
        </button>
        <button
          className={`brutal-btn text-xs px-4 py-2 ${tab === 'enemies' ? '' : 'opacity-50'}`}
          onClick={() => setTab('enemies')}
          data-testid="codex-tab-enemies">
          <Swords size={14} className="inline mr-1"/> ENEMIES
        </button>
        <button
          className={`brutal-btn text-xs px-4 py-2 ${tab === 'blessings' ? '' : 'opacity-50'}`}
          onClick={() => setTab('blessings')}
          data-testid="codex-tab-blessings">
          <Sparkles size={14} className="inline mr-1"/> BLESSINGS
        </button>
        <button
          className={`brutal-btn text-xs px-4 py-2 ${tab === 'discovered' ? '' : 'opacity-50'}`}
          onClick={() => setTab('discovered')}
          data-testid="codex-tab-discovered">
          <Trophy size={14} className="inline mr-1"/> DISCOVERED
        </button>
        <button
          className={`brutal-btn text-xs px-4 py-2 ${tab === 'divine' ? '' : 'opacity-50'}`}
          onClick={() => setTab('divine')}
          data-testid="codex-tab-divine"
          style={{ borderColor: '#fff2a8', color: tab === 'divine' ? '#fff2a8' : undefined }}>
          <Sparkles size={14} className="inline mr-1"/> DIVINE
        </button>
      </div>

      {tab === 'cards' && (
        <>
          <div className="mt-6 max-w-5xl mx-auto brutal-border-dim p-3 bg-[#0a0a0a] flex flex-wrap items-center gap-2 text-xs">
            <input
              className="bg-[#050505] border-2 border-[color:var(--neon)] px-3 py-1 text-white uppercase font-mono flex-1 min-w-40"
              placeholder="search card..." value={search} onChange={e => setSearch(e.target.value)}
              data-testid="codex-search"/>
            <select className="bg-[#050505] border-2 border-[color:var(--text-dim)] px-2 py-1 text-white font-mono uppercase"
              value={rarityFilter} onChange={e => setRarityFilter(e.target.value)}
              data-testid="codex-filter-rarity">
              {RARITIES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <select className="bg-[#050505] border-2 border-[color:var(--text-dim)] px-2 py-1 text-white font-mono uppercase"
              value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              data-testid="codex-filter-type">
              <option value="all">all types</option>
              <option value="attack">attack</option>
              <option value="skill">skill</option>
              <option value="power">power</option>
            </select>
            <select className="bg-[#050505] border-2 border-[color:var(--text-dim)] px-2 py-1 text-white font-mono uppercase"
              value={tagFilter} onChange={e => setTagFilter(e.target.value)}
              data-testid="codex-filter-tag">
              {TAGS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="ml-auto text-[color:var(--text-dim)] uppercase" data-testid="codex-card-count">
              {filteredCards.length}/{allCards.length}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-6xl mx-auto">
            {filteredCards.map(c => (
              <div key={c.id} className="flex flex-col items-center" data-testid={`codex-card-${c.id}`}>
                <GameCard cardId={c.id} disabled testid={`codex-card-preview-${c.id}`} />
                {c.upgradedId && (
                  <div className="mt-1 text-[9px] uppercase tracking-widest text-[color:var(--pink)]" title="Has an upgrade">+ UPGRADEABLE</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {tab === 'enemies' && (
        <>
          <div className="mt-4 text-center text-xs uppercase tracking-widest text-[color:var(--text-dim)]">
            defeated enemies unlock their dex entry
          </div>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-6xl mx-auto justify-items-center">
            {enemies.map(e => {
              const seen = entries.includes(e.id);
              const type = e.isBoss ? 'BOSS' : e.secret ? 'SECRET' : 'MOB';
              const accent = e.isBoss ? '#ff007f' : (e.secret ? '#39ff14' : '#ff2b2b');
              const l = seen ? loreFor(e.id) : null;
              const footer = seen ? (
                <div>
                  <div>INTENTS · {(e.intents || []).length}</div>
                  {l && <div className="text-[color:var(--neon)] mt-1">TIP · {l.tip}</div>}
                </div>
              ) : null;
              return (
                <CodexCard
                  key={e.id}
                  testid={`codex-entry-${e.id}`}
                  art={e.sprite}
                  name={e.name}
                  type={type}
                  cost={e.maxHp}
                  costLabel="HP"
                  costColor={accent}
                  desc={l?.flavor}
                  footer={footer}
                  accent={accent}
                  locked={!seen}
                />
              );
            })}
          </div>
        </>
      )}

      {/* v2.5 BLESSING ARCHIVE — codex tab showing all blessings + pick counts */}
      {tab === 'blessings' && (
        <div className="mt-6 max-w-5xl mx-auto" data-testid="codex-blessings-panel">
          {(() => {
            const pickCounts = profile?.blessing_pick_counts || {};
            const totalPicks = Object.values(pickCounts).reduce((a, b) => a + b, 0);
            const allBlessings = Object.values(BLESSINGS);
            const owned = allBlessings.filter(b => (pickCounts[b.id] || 0) > 0);
            return (
              <>
                <div className="text-xs uppercase font-mono tracking-widest text-[color:var(--text-dim)] mb-3 text-center" data-testid="codex-blessings-summary">
                  ARCHIVED · {owned.length} / {allBlessings.length} DISCOVERED · {totalPicks} TOTAL PICKS
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 justify-items-center">
                  {allBlessings.map(b => {
                    const count = pickCounts[b.id] || 0;
                    const seen = count > 0;
                    const color = RARITY_COLORS[b.rarity] || '#a0ff2e';
                    return (
                      <CodexCard
                        key={b.id}
                        testid={`codex-blessing-${b.id}`}
                        art={b.art}
                        name={b.name}
                        type={b.rarity}
                        desc={b.desc}
                        footer={seen ? <div>PICKED ×{count}</div> : null}
                        accent={color}
                        locked={!seen}
                      />
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}
      {/* v2.8 QoL: CARD DISCOVERY JOURNAL — track SEEN/PLAYED/UPGRADED */}
      {tab === 'discovered' && (
        <DiscoveryPanel allCards={allCards} />
      )}

      {/* v3.1: DIVINE COLLECTION TAB */}
      {tab === 'divine' && (
        <div className="mt-6 max-w-5xl mx-auto" data-testid="codex-divine-panel">
          <DivineCollection
            pickCounts={profile?.divine_pick_counts || {}}
            classFilter={null}
            showShare={true}
            playerTag={profile?.tag}
            avatar={profile?.avatar}
          />
        </div>
      )}
    </div>
  );
}

function DiscoveryPanel({ allCards }) {
  const [stats, setStats] = useState(() => readCardStats());
  useEffect(() => { setStats(readCardStats()); }, []);
  const totalCards = allCards.length;
  const seenCount = allCards.filter(c => (stats[c.id]?.seen || 0) > 0).length;
  const playedCount = allCards.filter(c => (stats[c.id]?.played || 0) > 0).length;
  const upgradedCount = allCards.filter(c => (stats[c.id]?.upgraded || 0) > 0).length;
  const totalPlays = Object.values(stats).reduce((a, s) => a + (s?.played || 0), 0);
  return (
    <div className="mt-6 max-w-5xl mx-auto" data-testid="codex-discovery-panel">
      <div className="text-xs uppercase font-mono tracking-widest text-[color:var(--text-dim)] mb-3 text-center" data-testid="discovery-summary">
        SEEN {seenCount}/{totalCards} · PLAYED {playedCount}/{totalCards} · UPGRADED {upgradedCount}/{totalCards} · {totalPlays} TOTAL PLAYS
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 justify-items-center">
        {allCards.map(c => {
          const s = stats[c.id] || { seen: 0, played: 0, upgraded: 0 };
          const seen = (s.seen || 0) > 0 || (s.played || 0) > 0;
          const color = seen ? '#39ff14' : '#333';
          const footer = seen ? (
            <div className="flex justify-between gap-2">
              <span data-testid={`discovery-seen-${c.id}`}>S×{s.seen || 0}</span>
              <span className="text-[color:var(--neon)]" data-testid={`discovery-played-${c.id}`}>P×{s.played || 0}</span>
              <span className="text-[color:var(--pink)]" data-testid={`discovery-upgraded-${c.id}`}>U×{s.upgraded || 0}</span>
            </div>
          ) : null;
          return (
            <CodexCard
              key={c.id}
              testid={`discovery-row-${c.id}`}
              art={c.art}
              name={c.name}
              type={c.type || 'CARD'}
              cost={c.cost}
              costLabel="COST"
              desc={c.desc}
              footer={footer}
              accent={color}
              locked={!seen}
            />
          );
        })}
      </div>
    </div>
  );
}

